import { LOGIN_PAGE_URL } from 'constants/utils';

import { Auth0ContextInterface, useAuth0, User } from '@auth0/auth0-react';
import {
  clearFilesSorts,
  deleteClientsListSize,
  deleteMobileInfoBannerClosed,
  getLsAccessToken,
  getRefreshTokenFromLSAuth0,
  removeClientListScrollPosition,
  setAutoLogoutRedirectUrl
} from 'modules/networkTools/localStorageTokens';
import { logout } from 'modules/networkTools/networkTools';
import { useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { deleteSearchQueryDraft } from 'store/myTodos/myTodosActions';

const SESSION_MARKER_KEY = 'coreBrowserSessionActive';
const SESSION_QUERY_KEY = 'coreBrowserSessionQuery';
const SESSION_REPLY_KEY = 'coreBrowserSessionReply';
const SESSION_CHANNEL_NAME = 'coreBrowserSessionChannel';
const SESSION_REPLY_WAIT_MS = 500;

const getRequestId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

type SessionMessage = {
  requestId: string;
  ts: number;
  type: 'query' | 'reply';
};

type SessionTransport = {
  close: () => void;
  postMessage: (message: SessionMessage) => void;
  subscribe: (handler: (message: SessionMessage) => void) => () => void;
};

const parseStoragePayload = (value: string | null): Partial<SessionMessage> => {
  if (!value) return {};

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const isSessionMessage = (payload: Partial<SessionMessage>): payload is SessionMessage => {
  return (
    typeof payload.requestId === 'string' &&
    typeof payload.ts === 'number' &&
    (payload.type === 'query' || payload.type === 'reply')
  );
};

const createStorageTransport = (): SessionTransport => ({
  close: () => undefined,
  postMessage: (message) => {
    const storageKey = message.type === 'query' ? SESSION_QUERY_KEY : SESSION_REPLY_KEY;
    localStorage.setItem(storageKey, JSON.stringify(message));
  },
  subscribe: (handler) => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== SESSION_QUERY_KEY && event.key !== SESSION_REPLY_KEY) {
        return;
      }

      const payload = parseStoragePayload(event.newValue);
      if (isSessionMessage(payload)) {
        handler(payload);
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => window.removeEventListener('storage', handleStorage);
  }
});

const createSessionTransport = (): SessionTransport => {
  if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);

    return {
      close: () => channel.close(),
      postMessage: (message) => channel.postMessage(message),
      subscribe: (handler) => {
        const handleMessage = (event: MessageEvent<SessionMessage>) => {
          if (isSessionMessage(event.data)) {
            handler(event.data);
          }
        };

        channel.addEventListener('message', handleMessage);

        return () => channel.removeEventListener('message', handleMessage);
      }
    };
  }

  return createStorageTransport();
};

export const useLogoutOnBrowserRestart = () => {
  const { logout: auth0logout }: Auth0ContextInterface<User> = useAuth0();
  const { pathname } = useLocation();
  const activeRequestIdRef = useRef<string | null>(null);
  const hasActiveTabRef = useRef(false);

  useEffect(() => {
    let logoutTimeout: ReturnType<typeof setTimeout> | null = null;
    const sessionTransport = createSessionTransport();
    const isExcludedPath =
      pathname === '/' ||
      pathname.includes('auth0') ||
      pathname.includes('permissions') ||
      pathname.includes('invite') ||
      pathname.includes('auth0Error');

    const markCurrentTabAsActive = () => {
      sessionStorage.setItem(SESSION_MARKER_KEY, 'true');
    };

    const isCurrentTabActive = () =>
      sessionStorage.getItem(SESSION_MARKER_KEY) === 'true' && Boolean(getLsAccessToken());

    const unsubscribe = sessionTransport.subscribe((message) => {
      if (message.type === 'query') {
        if (!isCurrentTabActive()) {
          return;
        }

        sessionTransport.postMessage({
          requestId: message.requestId,
          ts: Date.now(),
          type: 'reply'
        });
      }

      if (message.type === 'reply' && message.requestId === activeRequestIdRef.current) {
        hasActiveTabRef.current = true;
      }
    });

    const hasSessionMarker = sessionStorage.getItem(SESSION_MARKER_KEY) === 'true';
    const hasToken = Boolean(getLsAccessToken());

    if (isExcludedPath) {
      if (hasToken) {
        markCurrentTabAsActive();
      } else {
        sessionStorage.removeItem(SESSION_MARKER_KEY);
      }
    } else if (!hasToken) {
      sessionStorage.removeItem(SESSION_MARKER_KEY);
    } else if (!hasSessionMarker) {
      const requestId = getRequestId();
      activeRequestIdRef.current = requestId;
      hasActiveTabRef.current = false;

      sessionTransport.postMessage({ requestId, ts: Date.now(), type: 'query' });

      logoutTimeout = setTimeout(() => {
        if (hasActiveTabRef.current) {
          markCurrentTabAsActive();
          return;
        }

        auth0logout({
          logoutParams: {
            returnTo: window.location.origin + LOGIN_PAGE_URL
          }
        });
        logout();
      }, SESSION_REPLY_WAIT_MS);
    } else {
      markCurrentTabAsActive();
    }

    return () => {
      unsubscribe();
      sessionTransport.close();
      if (logoutTimeout) {
        clearTimeout(logoutTimeout);
      }
    };
  }, [auth0logout, pathname]);
};

export const useAutoLogout = <T extends () => void>(idleTime: number, disabled: boolean) => {
  const dispatch = useAppDispatch();
  const { logout: auth0logout }: Auth0ContextInterface<User> = useAuth0();
  const [searchParams] = useSearchParams();

  const context = searchParams.get('context');
  const handleLogout = async () => {
    const lsRefreshToken = getRefreshTokenFromLSAuth0('auth0spajs');

    if (context === 'popup') {
      window.close();
      return;
    }
    localStorage.removeItem('lastPage');
    removeClientListScrollPosition();
    deleteClientsListSize();
    deleteMobileInfoBannerClosed();
    clearFilesSorts();
    if (lsRefreshToken) {
      await dispatch(deleteSearchQueryDraft());
      setAutoLogoutRedirectUrl(window.location.pathname);
      auth0logout({
        logoutParams: {
          returnTo: window.location.origin + LOGIN_PAGE_URL
        }
      });
      logout();
    }
  };

  useIdleTimer({
    timeout: idleTime * 60_000,
    onIdle: handleLogout,
    debounce: 0,
    crossTab: true,
    disabled,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange',
      'focus'
    ]
  });
};
